import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';

const programs = [
    {
        id: 'lifting-for-two',
        name: 'Lifting for Two',
        tagline: 'Pregnancy strength training for experienced lifters',
        description: "You didn't stop being strong when you got pregnant. A training framework for women who already lift — and have no intention of starting over.",
        status: 'Coming Soon',
        statusColor: 'bg-terra/10 text-terra',
        link: '/programs/lifting-for-two',
        isExternal: false,
    },
    // Future programs will be added here
];

const ProgramsPage = () => {
    return (
        <HelmetProvider>
            <div className="flex-1 bg-cream text-ink">
                <Helmet prioritizeSeoTags>
                    <title>Programs — Amanda Filomena | Fitness & Systems</title>
                    <link rel="canonical" href="https://amandafilomena.com/programs" />
                    <meta name="description" content="Fitness and productivity programs by Amanda Filomena. Systems that work for ambitious women with real constraints." />
                </Helmet>

                <main className="mx-auto max-w-6xl px-6 py-20 md:py-28">
                    {/* Hero */}
                    <div className="mb-16 opacity-0 animate-fade-up">
                        <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-4">What I'm Building</p>
                        <h1 className="text-3xl md:text-4xl text-ink mb-4" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>
                            Programs
                        </h1>
                        <p className="text-base text-muted max-w-xl leading-relaxed">
                            Systems that hold through hard seasons. Built for ambitious women who don't have hours to spare.
                        </p>
                    </div>

                    {/* Programs Grid */}
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        {programs.map((program) => {
                            const CardWrapper = program.isExternal ? 'a' : Link;
                            const linkProps = program.isExternal
                                ? { href: program.link, target: '_blank', rel: 'noopener noreferrer' }
                                : { to: program.link };

                            return (
                                <CardWrapper
                                    key={program.id}
                                    {...linkProps}
                                    className="group block bg-surface border border-sand p-8 md:p-10 transition-all hover:border-terra/40 hover:shadow-sm"
                                >
                                    <div className="flex items-center justify-between mb-6">
                                        <span className={`px-3 py-1 text-xs font-medium uppercase tracking-wider ${program.statusColor}`}>
                                            {program.status}
                                        </span>
                                    </div>
                                    <h2 className="text-xl md:text-2xl mb-3 text-ink" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>
                                        {program.name}
                                    </h2>
                                    <p className="text-sm text-terra mb-4">{program.tagline}</p>
                                    <p className="text-sm text-muted leading-relaxed mb-6">{program.description}</p>
                                    <p className="text-xs font-medium uppercase tracking-wider text-terra opacity-0 group-hover:opacity-100 transition-opacity">
                                        Learn More →
                                    </p>
                                </CardWrapper>
                            );
                        })}

                        {/* Placeholder for future program */}
                        <div className="bg-surface/50 border border-dashed border-sand p-8 md:p-10 flex flex-col items-center justify-center text-center">
                            <p className="text-xs font-medium tracking-[0.2em] text-muted uppercase mb-4">More Coming</p>
                            <p className="text-sm text-muted">
                                Additional programs in development. Follow along on{' '}
                                <a
                                    href="https://www.instagram.com/amandafilomena_/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-terra hover:underline"
                                >
                                    Instagram
                                </a>{' '}
                                for updates.
                            </p>
                        </div>
                    </div>
                </main>

                {/* CTA Section */}
                <section className="bg-ink px-6 py-16">
                    <div className="mx-auto max-w-6xl flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <div>
                            <h2 className="text-2xl text-cream mb-2" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>
                                Want gear recommendations instead?
                            </h2>
                            <p className="text-cream/60 text-sm">
                                Check out my curated storefront for fitness, supplements, and equipment.
                            </p>
                        </div>
                        <Link
                            to="/storefront"
                            className="inline-flex items-center justify-center bg-terra px-8 py-4 text-xs font-medium uppercase tracking-wider text-cream transition-all hover:bg-terra/90"
                        >
                            View Storefront
                        </Link>
                    </div>
                </section>

                {/* Footer */}
                <footer className="border-t border-sand px-6 py-8">
                    <div className="mx-auto max-w-6xl flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <p className="text-sm text-muted">© 2026 Amanda Filomena</p>
                        <div className="flex gap-6">
                            <a href="https://github.com/amandafc-BYU" target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-wider text-muted hover:text-terra transition-colors">
                                GitHub
                            </a>
                            <a href="mailto:hello@amandafilomena.com" className="text-xs uppercase tracking-wider text-muted hover:text-terra transition-colors">
                                Contact
                            </a>
                        </div>
                    </div>
                </footer>
            </div>
        </HelmetProvider>
    );
};

export default ProgramsPage;
